import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageBreadcrumb from '@/components/common/PageBreadCrumb';
import PageMeta from '@/components/common/PageMeta';
import { useRenting } from '@/features/rentings/hooks/useRentings';
import { useReport, useRentingReports } from '@/features/cars/hooks/useCarReports';
import EditCarReportForm from '@/features/cars/components/EditCarReportForm';
import { useQueryClient } from '@tanstack/react-query';
import { ReportsListResponse } from '@/features/cars/api/carReportsApi';
import { CarModel } from '@/features/rentings/api/rentingsApi';

const EditReportPage: React.FC = () => {
  const { id: rentingId, reportId } = useParams<{ id: string; reportId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: renting, isLoading: isLoadingRenting, error: rentingError } = useRenting(rentingId!);
  const { data: report, isLoading: isLoadingReport, error: reportError } = useReport(reportId!);
  const { data: reports } = useRentingReports(rentingId || '');

  const [reportForEdit, setReportForEdit] = useState<ReportsListResponse['data'][0] | null>(null);

  useEffect(() => {
    if (report && reports) {
      // Находим отчет в списке или создаем из данных
      const foundReport = reports.find(r => r.id === reportId);

      if (foundReport) {
        setReportForEdit(foundReport);
      } else {
        // Создаем объект отчета из данных
        const reportData: ReportsListResponse['data'][0] = {
          id: report.id,
          car: {
            id: report.car.id,
            slug: report.car.slug || '',
            color: report.car.color,
            model: report.car.model as unknown as CarModel,
            release_year: report.car.release_year,
            registration_number: report.car.registration_number,
            description: (report.car as any).description_en || (report.car as any).description || '',
            price: report.car.price || '',
            image: report.car.image,
            image_url: report.car.image_url
          },
          renting: {
            ...report.renting,
            client: {
              ...report.renting.client,
              client_type: report.renting.client.client_type || '',
              post_index: report.renting.client.post_index || '',
              country: report.renting.client.country || '',
              city: report.renting.client.city || '',
              address: report.renting.client.address || '',
              language: report.renting.client.language || '',
              verification_status: report.renting.client.verification_status || '',
              is_email_confirmed: report.renting.client.is_email_confirmed || false,
              is_phone_confirmed: report.renting.client.is_phone_confirmed || false,
              registered_at: report.renting.client.registered_at || report.renting.created_at
            }
          },
          activity: report.activity,
          tech_profile: report.tech_profile,
          employee: report.employee,
          mileage: report.mileage,
          fuel_level: report.fuel_level,
          charge_level: report.charge_level,
          created_at: report.created_at,
          details: report.details
        };
        setReportForEdit(reportData);
      }
    }
  }, [report, reports, reportId]);

  if (isLoadingRenting || isLoadingReport) {
    return <div>{t('Loading...')}</div>;
  }

  if (rentingError || reportError || !renting || !report || !reportForEdit) {
    return <div>{t('Error loading data')}</div>;
  }

  const handleSubmit = () => {
    queryClient.invalidateQueries({ queryKey: ['reports', rentingId] });
    queryClient.invalidateQueries({ queryKey: ['report', reportId] });
    navigate(`/rentings/${rentingId}`);
  };

  const handleClose = () => {
    navigate(`/rentings/${rentingId}`);
  };

  return (
    <>
      <PageMeta
        title={`${t('cars.report.edit.title')} | Admin.Drivitive`}
        description={t('cars.report.edit.description')}
      />
      <PageBreadcrumb
        items={[
          { label: t('nav.rentings'), path: '/rentings' },
          { label: `${t('rentings.breadcrumb.renting')} #${renting.number}`, path: `/rentings/${rentingId}` },
          { label: t('cars.report.edit.title') },
        ]}
      />

      <div className="mx-auto">
        <EditCarReportForm
          report={reportForEdit}
          onSubmit={handleSubmit}
          onClose={handleClose}
        />
      </div>
    </>
  );
};

export default EditReportPage;
